/**
 * useResilientMutation — a mutation that asks the server what happened instead of
 * believing a lost response.
 *
 * WHY THIS EXISTS. Measured in production on 2026-08-13: a user on an aeroplane wifi
 * clicked "Create & Populate", got a red "Request timeout" banner, and the exploration had
 * fully succeeded (completed, 7/7 concepts grounded, 195 groundings, 15 instances). The route
 * is already asynchronous and answers in 0.55s when measured directly; what the flaky link
 * lost was the HTTP RESPONSE, not the work. The UI then reported failure on a success, and the
 * only affordance it offered was to click again, which creates a second exploration. That is
 * the established cause of the twin `catalyse-hydrogene` explorations: two projections of one
 * corpus, indistinguishable on every exposed field.
 *
 * So: on a failure that means "I do not know whether it landed", read the server truth and
 * decide from that. Server-side idempotency keys were considered and deliberately not chosen
 * for this change, so nothing here touches the API contract.
 */
import { useCallback, useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError } from '@/lib/api/client';

/**
 * How many times verification is attempted before the UI gives up and says so.
 *
 * Three, not one, for two different reasons that happen to want the same knob:
 *  * the verification GET travels the same broken link that ate the response, so it can fail
 *    for reasons that say nothing about the work;
 *  * a "not there" answer is not instantly conclusive. The client aborted at its own timeout
 *    while the server may still have been committing, so the row can appear a moment later.
 *    Re-asking twice over ~4s covers that race instead of pretending the first read is truth.
 */
export const VERIFY_ATTEMPTS = 3;

/**
 * Per-attempt deadline for a verification read.
 *
 * The api client's own timeout is `env.apiTimeout` (30s by default), which is the right budget
 * for work but far too long here: three attempts at 30s each would leave the user in front of
 * a spinner for over two minutes before being told anything. Verification passes its own
 * AbortSignal, which the client uses INSTEAD of its 30s controller, so the whole verification
 * window is bounded at 3 * 8s + 2 * 2s = 28s worst case.
 */
export const VERIFY_ATTEMPT_TIMEOUT_MS = 8_000;

/** Pause between verification attempts. Also the grace given to a late commit. */
export const VERIFY_RETRY_DELAY_MS = 2_000;

/**
 * Raised when verification could not answer at all. This is NOT "the work failed" and NOT
 * "the work succeeded": it is the honest third state, and the UI has to say exactly that.
 * Turning it into either of the other two is how a verification mechanism starts lying.
 */
export class UnconfirmedMutationError extends Error {
  constructor(
    /** The transport failure that started this: what the user would otherwise have seen. */
    readonly originalError: unknown,
    /** Why verification could not answer, last attempt. */
    readonly verifyError: unknown,
  ) {
    super('Could not confirm whether the operation went through');
    this.name = 'UnconfirmedMutationError';
  }
}

/**
 * True when a failure leaves the outcome of the request genuinely unknown.
 *
 * The api client already classifies precisely, which is the whole signal this needs:
 *  * `TIMEOUT` (408) — the client-side abort controller fired. The request may have been
 *    fully served with the response lost on the way back.
 *  * `NETWORK_ERROR` (0) — fetch could not complete. Same ambiguity in the other direction.
 *
 * A 4xx is a definitive refusal by a server that clearly received the request: verifying a
 * validation error would be pure noise, and worse, it would teach the code that "error" and
 * "maybe" are the same thing.
 *
 * 5xx IS treated as unknown, deliberately. The server answered, so the request arrived; that
 * makes a partial application possible, not impossible. Two concrete shapes seen on this
 * stack: nginx/Code Engine returning 502 or 504 when the app takes too long to answer a
 * request it is already serving, and a handler that inserted the row then threw on a later
 * statement. Both leave work on disk behind an error response. Verification is a read-only GET
 * whose worst case is telling the user the truthful "it did not land"; not verifying costs a
 * duplicate exploration, which is a data-quality defect a human then has to untangle. The
 * asymmetry decides it.
 *
 * Also matches the raw `AbortError` / `TypeError` pair, because `knowledgeBaseApi.uploadDocument`
 * needs FormData and so calls `fetch` directly instead of going through the api client. Its
 * failures never get wrapped into an `ApiError`, and upload is one of the mutations that must
 * be covered.
 */
export function isUnknownOutcome(error: unknown): boolean {
  if (error instanceof ApiError) {
    if (error.code === 'TIMEOUT' || error.code === 'NETWORK_ERROR') return true;
    return typeof error.status === 'number' && error.status >= 500;
  }
  // Raw fetch failures from the paths that bypass the api client.
  if (error instanceof TypeError) return true;
  if (typeof DOMException !== 'undefined' && error instanceof DOMException) {
    return error.name === 'AbortError';
  }
  return false;
}

/** Passed to every `verify` call so the read is bounded and can adapt to the attempt number. */
export interface VerifyContext {
  /** Aborts at `VERIFY_ATTEMPT_TIMEOUT_MS`. Forward it to the api call. */
  signal: AbortSignal;
  /** 1-based. */
  attempt: number;
}

/**
 * Reads server truth for one mutation.
 *
 * Return the landed resource, in the shape the success path expects, when the work is there.
 * Return null/undefined/false when the server clearly answered and the work is NOT there.
 * Throw when the read itself failed, which is a different thing and is retried.
 */
export type VerifyFn<TData, TVars> = (
  vars: TVars,
  ctx: VerifyContext,
) => Promise<TData | null | undefined | false>;

export interface ResilientMutationOptions<TData, TVars> {
  mutationFn: (vars: TVars) => Promise<TData>;
  /** Omit for a mutation that cannot be verified honestly: it then behaves like a plain one. */
  verify?: VerifyFn<TData, TVars>;
  /**
   * The single success path, run identically whether the response arrived or the work was
   * recovered by verification. `verified` is there for wording, never for branching logic.
   */
  onSuccess?: (data: TData, vars: TVars, meta: { verified: boolean }) => void | Promise<unknown>;
  onError?: (error: Error, vars: TVars) => void;
}

/** What the mutation resolves with internally, so the success path can tell the two apart. */
interface Landed<TData> {
  data: TData;
  verified: boolean;
}

type VerifyOutcome<TData> =
  | { state: 'landed'; data: TData }
  | { state: 'absent' }
  | { state: 'unconfirmed'; error: unknown };

const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Runs `verify` up to `VERIFY_ATTEMPTS` times.
 *
 * A "not there" answer is retried like a failure, because the client's abort does not stop the
 * server: the row can land a moment after we gave up waiting. Only the last word counts, and
 * the difference between "every attempt said no" (absent) and "no attempt could answer"
 * (unconfirmed) is tracked explicitly, because collapsing them is how a genuine failure would
 * become a silent success.
 */
async function runVerification<TData, TVars>(
  verify: VerifyFn<TData, TVars>,
  vars: TVars,
): Promise<VerifyOutcome<TData>> {
  let answered = false;
  let lastError: unknown;

  for (let attempt = 1; attempt <= VERIFY_ATTEMPTS; attempt++) {
    if (attempt > 1) await delay(VERIFY_RETRY_DELAY_MS);

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), VERIFY_ATTEMPT_TIMEOUT_MS);
    try {
      const found = await verify(vars, { signal: controller.signal, attempt });
      if (found) return { state: 'landed', data: found };
      answered = true;
    } catch (error) {
      lastError = error;
    } finally {
      clearTimeout(timer);
    }
  }

  return answered ? { state: 'absent' } : { state: 'unconfirmed', error: lastError };
}

export interface ResilientMutation<TData, TVars> {
  /** Fire the mutation. Resolves with the data on success, null otherwise; never throws. */
  submit: (vars: TVars) => Promise<TData | null>;
  /**
   * Retry affordance. Re-verifies before resubmitting, so the button offered next to an error
   * cannot itself be the thing that creates the duplicate.
   */
  retry: () => Promise<TData | null>;
  /** True from the click until the outcome is known, verification included. */
  isPending: boolean;
  /** True only while asking the server what happened. Drives the wording, not the disabling. */
  isVerifying: boolean;
  /** Verification could not answer: neither success nor failure may be claimed. */
  isUnconfirmed: boolean;
  /** Set when the outcome is a genuine failure, or unconfirmed. */
  error: Error | null;
  data: TData | null;
  /** Variables of the attempt in flight (or the last one), for per-row spinners. */
  variables: TVars | null;
  isSuccess: boolean;
  /** True when there is something to retry. */
  canRetry: boolean;
  reset: () => void;
}

export function useResilientMutation<TData, TVars>(
  options: ResilientMutationOptions<TData, TVars>,
): ResilientMutation<TData, TVars> {
  const [isVerifying, setIsVerifying] = useState(false);
  const lastVars = useRef<{ vars: TVars } | null>(null);

  // Options are read through a ref so the mutation never goes stale on a re-render, which
  // matters because callers build `verify` as a closure over the rows currently on screen.
  const optionsRef = useRef(options);
  optionsRef.current = options;

  const mutation = useMutation<Landed<TData>, Error, { vars: TVars; verifyFirst: boolean }>({
    // VERIFICATION RUNS INSIDE mutationFn, ON PURPOSE.
    //
    // `onError` was the obvious place and is the wrong one: TanStack has already moved the
    // mutation to `error` by the time it runs, and there is no way back to `success`. The
    // caller's `isError` would stay true while we quietly did the success work, so the UI
    // would show a red banner over a completed operation, which is the bug being fixed.
    // A wrapper around `mutateAsync` has the mirror problem: `isPending` would be false while
    // verification runs, so the button would re-enable and the user could fire the duplicate
    // in exactly the window this exists to close.
    //
    // Inside `mutationFn` there is only one success path, shared byte for byte between "the
    // response arrived" and "the response was lost but the work is there", and `isPending`
    // covers the whole window.
    mutationFn: async ({ vars, verifyFirst }) => {
      const { mutationFn, verify } = optionsRef.current;

      if (verifyFirst && verify) {
        setIsVerifying(true);
        try {
          const outcome = await runVerification(verify, vars);
          if (outcome.state === 'landed') return { data: outcome.data, verified: true };
          if (outcome.state === 'unconfirmed') {
            // Refuse to resubmit while blind. The point of this whole mechanism is that a
            // duplicate is worse than a delay: if the work HAD landed, a read that cannot
            // answer cannot rule it out, so pressing send again is exactly the gamble we are
            // removing. The retry affordance stays, and re-verifies on the next press.
            throw new UnconfirmedMutationError(outcome.error, outcome.error);
          }
        } finally {
          setIsVerifying(false);
        }
      }

      try {
        return { data: await mutationFn(vars), verified: false };
      } catch (error) {
        if (!verify || !isUnknownOutcome(error)) throw error;

        setIsVerifying(true);
        try {
          const outcome = await runVerification(verify, vars);
          if (outcome.state === 'landed') return { data: outcome.data, verified: true };
          if (outcome.state === 'unconfirmed') {
            throw new UnconfirmedMutationError(error, outcome.error);
          }
          // The server answered and the work is not there: the original transport error is
          // the honest thing to show, not a paraphrase of it.
          throw error;
        } finally {
          setIsVerifying(false);
        }
      }
    },

    // Blind mutation retries are the other half of the duplicate problem. The query client
    // sets `mutations: { retry: 1 }` globally, so a create that timed out was silently
    // re-POSTed a second later with no idea whether the first one landed: a duplicate
    // generator sitting in the defaults. Verification replaces it.
    retry: false,

    onSuccess: (landed, { vars }) =>
      optionsRef.current.onSuccess?.(landed.data, vars, { verified: landed.verified }),
    onError: (error, { vars }) => optionsRef.current.onError?.(error, vars),
  });

  const run = useCallback(
    async (vars: TVars, verifyFirst: boolean): Promise<TData | null> => {
      lastVars.current = { vars };
      try {
        const landed = await mutation.mutateAsync({ vars, verifyFirst });
        return landed.data;
      } catch {
        // The outcome lives in `error` / `isUnconfirmed`; callers render from state.
        return null;
      }
    },
    [mutation],
  );

  const submit = useCallback((vars: TVars) => run(vars, false), [run]);

  const retry = useCallback(() => {
    const previous = lastVars.current;
    if (!previous) return Promise.resolve(null);
    return run(previous.vars, true);
  }, [run]);

  const reset = useCallback(() => {
    lastVars.current = null;
    mutation.reset();
  }, [mutation]);

  return {
    submit,
    retry,
    isPending: mutation.isPending,
    isVerifying,
    isUnconfirmed: mutation.error instanceof UnconfirmedMutationError,
    error: mutation.error ?? null,
    data: mutation.data?.data ?? null,
    variables: mutation.variables?.vars ?? null,
    isSuccess: mutation.isSuccess,
    canRetry: !!mutation.error && lastVars.current !== null,
    reset,
  };
}

/**
 * The ids a list held before the mutation went out.
 *
 * Every verify here answers "did the server create the row I asked for" by looking for an id
 * it had not seen, never by comparing `created_at` to `Date.now()`. The reported case is a
 * laptop on an aeroplane: assuming the browser clock agrees with Postgres would make the check
 * least reliable exactly where it is needed. It also keeps a pre-existing near-identical row
 * (the twin-exploration shape) from being mistaken for the one we just asked for.
 */
export function snapshotIds(rows: readonly { id: string }[] | undefined): ReadonlySet<string> {
  return new Set((rows ?? []).map((row) => row.id));
}

/**
 * Human wording for an outcome, so every call site says the same thing.
 * `null` means there is nothing to show.
 */
export function outcomeMessage(error: Error | null): string | null {
  if (!error) return null;
  if (error instanceof UnconfirmedMutationError) {
    return 'We could not reach the server to check whether this went through, so nothing was sent again. Check your connection and press Retry: it will look again before resubmitting.';
  }
  return error.message;
}
